import { configure, default as SentryFetch } from "@amzn/sentry-fetch";

/**
 * Detects whether the app is running inside the Cypress E2E test runner.
 *
 * SentryFetch 4.0 probes `midway-auth.amazon.com/SSO/redirect?response_type=none`
 * to validate an interactive Midway session before priming auth cookies. Under
 * SAAM-authenticated Cypress/Hydra runs there is no interactive Midway session,
 * so the probe returns 401 even though backend authentication (the app-domain
 * session cookie, validated server-side by API Gateway) is valid. SentryFetch
 * exposes `skipMidwaySessionCheck` for exactly this case: the no-CORS cookie
 * prime path still runs and a genuine failure still surfaces as
 * `AUTHENTICATION_FAILED`.
 *
 * This is gated to the test runner only — production users keep the session
 * probe and the step-up modal.
 */
const isCypressTestRun = (): boolean =>
  typeof window !== "undefined" && Boolean((window as Window & { Cypress?: unknown }).Cypress);

/**
 * This service will be used to ensure Midway authentication is performed while trying to fetch
 * API resources
 */
export class BaseService {
  protected sentryFetch: typeof SentryFetch;
  protected baseUrl: string;

  constructor(baseUrl: string) {
    this.sentryFetch = configure({
      debug: false,
      followMidwayStepUp: true,
      failOnInvalidSession: true,
      skipMidwaySessionCheck: isCypressTestRun(),
    });
    this.baseUrl = baseUrl;
  }

  public async fetch(fullPath: string, init?: RequestInit): Promise<Response> {
    const normalizedPath = fullPath.startsWith("/") ? fullPath : `/${fullPath}`;
    const url = `${this.baseUrl}${normalizedPath}`;

    return await this.sentryFetch(url, {
      credentials: "include",
      mode: "cors",
      ...init,
    });
  }
}